import { useMemo, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useStore } from 'utils/store'
import {
  alpha,
  AppBar as MuiAppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  Link,
  styled,
  Toolbar,
  Stack,
  Typography,
  Tab,
} from '@mui/material'
import { Menu, CloseRounded } from '@mui/icons-material'

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}))

export const NavItem = ({ link, label, color, style }) => {
  const location = useLocation()
  const isActive = useMemo(() => location.pathname === link, [location, link])

  return isActive ? (
    <Typography variant='h5' color='primary' m='0 !important' px={2}>
      {label}
    </Typography>
  ) : (
    <Link component={RouterLink} to={link} sx={{ m: '0 !important' }}>
      <Tab
        color='primary'
        label={label}
        sx={{
          color: color,
          fontSize: '14px',
          textTransform: 'uppercase',
          ...style,
        }}>
        {label}
      </Tab>
    </Link>
  )
}

export default function AppBar({ children, title }) {
  const isMobile = useStore(state => state.isMobile)
  const [open, setOpen] = useState(false)

  const toggleDrawer = newOpen => () => setOpen(newOpen)

  return (
    <MuiAppBar
      position='fixed'
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}>
      <Container maxWidth='lg'>
        <StyledToolbar variant='dense' disableGutters>
          {title}
          {isMobile ? (
            <Stack gap={1}>
              <IconButton aria-label='Menu button' onClick={toggleDrawer(true)}>
                <Menu />
              </IconButton>
              <Drawer
                anchor='top'
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    top: 'var(--template-frame-height, 0px)',
                  },
                }}>
                <Stack sx={{ p: 2, backgroundColor: 'background.default' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={toggleDrawer(false)}>
                      <CloseRounded />
                    </IconButton>
                  </Box>
                  {children}
                </Stack>
              </Drawer>
            </Stack>
          ) : (
            <Stack flexDirection='row' alignItems='center' flex={1} spacing={1}>
              {children}
            </Stack>
          )}
        </StyledToolbar>
      </Container>
    </MuiAppBar>
  )
}
